import {match} from 'ts-pattern';

import {ascend, sortWith} from 'ramda';

import {UnifiedAlbumImage} from '../types/UnifiedAlbumImage';

// BE sends promo photo `position` per source (campaign / own) independently, so two photos can
// legitimately claim the same slot, and a source can skip ahead leaving a gap. Comparing `position`
// directly against a real album photo's `weight` (a third, unrelated counter) would let raw numeric
// coincidences decide placement, so real photos are placed by relative order only, filling whichever
// slots promo photos left free - mirrors the slot-bumping in backend's RenderedSaleVehiclePhotosFilesQuery
// so the DMS gallery order matches the advertised one.
const getSourcePriority = (image: UnifiedAlbumImage) =>
  match(image)
    .with({isCampaignPhoto: true}, () => 0)
    .with({isPromoPhoto: true}, () => 1)
    .otherwise(() => 2);

const placeImages = (
  takenSlots: ReadonlyMap<number, UnifiedAlbumImage>,
  images: UnifiedAlbumImage[],
  desiredSlot: (image: UnifiedAlbumImage, nextSlot: number) => number
) => {
  const slots = new Map(takenSlots);
  let nextSlot = 1;
  images.forEach((image) => {
    let slot = desiredSlot(image, nextSlot);
    while (slots.has(slot)) {
      slot += 1;
    }
    slots.set(slot, image);
    nextSlot = slot + 1;
  });
  return slots;
};

export const mergeAlbumAndPromoImages = (
  albumImages: UnifiedAlbumImage[],
  promoPhotos: UnifiedAlbumImage[]
) => {
  const sortedPromoPhotos = sortWith(
    [
      ascend((image: UnifiedAlbumImage) => image.position ?? image.weight),
      ascend(getSourcePriority),
    ],
    promoPhotos
  ).map((promoPhoto) => ({...promoPhoto, isPromoPhoto: true}));
  const sortedAlbumImages = sortWith(
    [ascend((image: UnifiedAlbumImage) => image.weight)],
    albumImages
  );

  const promoSlots = placeImages(new Map(), sortedPromoPhotos, (image, nextSlot) =>
    Math.max(nextSlot, image.position ?? nextSlot)
  );
  const slots = placeImages(promoSlots, sortedAlbumImages, (_, nextSlot) => nextSlot);

  return [...slots.entries()].sort(([a], [b]) => a - b).map(([, image]) => image);
};
